import { authenticatedFetch, useCurrentUser, type PlanInterface } from "@beholos/holoscommon";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useState } from "react";
import { /* CircleAlert, ExternalLink, */ InfoIcon } from "lucide-react";
import type { PromoCode } from "./PaymentModal";
// import { SMART_FIT_TERMS_URL, type SmartFitDuration } from "./smartFit.constants";
import { PaymentSummaryCard } from "./PaymentSummaryCard";



export const PaymentModalStep1 = ({
  selectedPlan,
  price,
  fullPrice,
  discount,
  paymentDate,
  selectedPaymentOption,
  setPaymentDateToday,
  setPaymentDateNextCycle,
  appliedPromo,
  setAppliedPromo,
  // isSmartFitSelected,
  // setIsSmartFitSelected,
  // smartFitDuration,
  // setSmartFitDuration,
  // smartFitPrice,
}: {
  selectedPlan: PlanInterface;
  price: number | undefined;
  fullPrice: number | undefined;
  discount: number | undefined;
  paymentDate: Date;
  selectedPaymentOption: 'today' | 'nextCycle';
  setPaymentDateToday: () => void;
  setPaymentDateNextCycle: () => void;
  appliedPromo: PromoCode | null;
  setAppliedPromo: (promo: PromoCode | null) => void;
  // isSmartFitSelected: boolean;
  // setIsSmartFitSelected: (selected: boolean) => void;
  // smartFitDuration: SmartFitDuration;
  // setSmartFitDuration: (duration: SmartFitDuration) => void;
  // smartFitPrice: number;
}) => {
  const { user } = useCurrentUser();
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);

  const hasUpgradedPlan = user?.hasPlan && user?.plan?.id !== user?.plan?.basePlan?.id;

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoError(null);
    setAppliedPromo(null);
    try {
      const res = await authenticatedFetch(`/api/payments/promo/${promoCode.trim()}`);
      if (!res.ok) {
        setPromoError('Código no válido o no encontrado.');
        return;
      }
      const promo: PromoCode = await res.json();
      if (promo.firstTimeUsersOnly && hasUpgradedPlan) {
        setPromoError('Este código es solo para nuevos usuarios.');
        return;
      }

      if (promo.excludedPlans?.includes(selectedPlan._id)) {
        setPromoError('Este código no es válido para el plan seleccionado.');
        return;
      }
      setAppliedPromo(promo);
    } catch {
      setPromoError('No pudimos verificar el código. Intenta de nuevo.');
    } finally {
      setPromoLoading(false);
    }
  };

  const holosMonthlyCharge = Math.max(0, (fullPrice ?? 0) - (discount ?? 0));
  const promoDiscount = appliedPromo ? holosMonthlyCharge * appliedPromo.discountPercent : 0;
  const finalTitleBeforeSmartFit = Math.max(0, holosMonthlyCharge - promoDiscount);
  const finalTotal = Math.max(0, holosMonthlyCharge - promoDiscount)/*  + smartFitPrice */;
  const chargeToday = appliedPromo ? finalTotal : (price ?? 0);

  return (
    <div className="p-6 w-full mx-auto flex-1 overflow-y-auto">
      <div className="font-medium text-4xl font-juana mb-3 border-b border-gray-400">
        Confirma tu {selectedPlan ? 'plan' : 'compra'}
      </div>

      <div className="text-lg font-bold">Resumen de tu compra</div>

      <div className="mt-5 bg-holos-light border-1 border-gray-400 rounded-xl p-4">
        <div>{selectedPlan?.name}</div>
        <div>
          {appliedPromo ? (
            <span className="flex items-baseline gap-2">
              <span className="text-sm font-normal text-gray-400 line-through">S/ {fullPrice?.toFixed(2)}</span>
              <span>S/ {finalTitleBeforeSmartFit.toFixed(2)}</span>
            </span>
          ) : (
            <span>S/ {holosMonthlyCharge.toFixed(2)}</span>
          )}
        </div>
        {selectedPlan && user?.isCorporate && selectedPlan.cycleDuration === 1 && <div className="font-grey3 my-3 text-xs flex items-center gap-1.5">
          <InfoIcon className="mr-1 text-sm font-grey3" />
          Recuerda que este monto, se renovará automáticamente de tu tarjeta débito/crédito cada mes.
        </div>}
      </div>


      {selectedPlan && user?.hasPlan && <div className="my-6">
        <div className="text-lg font-bold border-b border-gray-400 mb-2">Elige la fecha de cambio</div>
        <label className="flex mb-4 cursor-pointer items-start" htmlFor="today">
          <input
            type="radio"
            name="paymentDate"
            value="today"
            id="today"
            onChange={setPaymentDateToday}
            checked={selectedPaymentOption === 'today'}
            className="form-radio text-black accent-black h-6 w-6 flex-shrink-0"
          />
          <span className="ml-2 text-sm">
            <strong>
              Hoy:
              <br />
              Efectivo de inmediato.
            </strong>
            <br />
            - El cobro se hace hoy.
            <br />
            - Tu siguiente renovación será con el mismo plan.
          </span>
        </label>
        <label className="flex mb-4 cursor-pointer items-start" htmlFor="nextCycle">
          <input
            type="radio"
            name="paymentDate"
            value="nextCycle"
            id="nextCycle"
            onChange={setPaymentDateNextCycle}
            checked={selectedPaymentOption === 'nextCycle'}
            className="form-radio text-black accent-black h-6 w-6 flex-shrink-0"
          />
          <span className="ml-2 text-sm">
            <strong>
              Próximo ciclo (cambio al renovar):
              <br />
              Efectivo desde el
              {format(new Date(user?.nextCycle || new Date()), ' dd', { locale: es })}
              <span> de </span>
              {format(new Date(user?.nextCycle || new Date()), 'MMMM', { locale: es })}
              <span> de </span>
              {format(new Date(user?.nextCycle || new Date()), 'yyyy', { locale: es })}
            </strong>
            <br />
            -Tu plan cambiará al finalizar tu ciclo actual.
          </span>
        </label>
      </div>}

      <div className="mt-5 mb-2">
        <div className="text-lg font-bold border-b border-gray-400">
          <span>A pagar - {selectedPaymentOption === 'today' ? 'Hoy' : 'Siguiente ciclo'} </span>
          {format(new Date(paymentDate), 'dd', { locale: es })}
          <span> de </span>
          {format(new Date(paymentDate), 'MMMM', { locale: es })}
          <span> de </span>
          {format(new Date(paymentDate), 'yyyy', { locale: es })}
        </div>
      </div>

      <div className="w-full">
        <div className="mb-2 text-xs font-bold">Cupón de descuento:</div>
        <div className="w-full flex gap-4">
          <input
            type="text"
            value={promoCode}
            onChange={e => { setPromoCode(e.target.value); setPromoError(null); setAppliedPromo(null); }}
            onKeyDown={e => e.key === 'Enter' && validatePromo()}
            placeholder="Ingresa tu cupón aquí"
            className="border border-gray-400 rounded-full pl-5 flex-grow py-1 text-sm"
          />
          <button
            onClick={validatePromo}
            disabled={promoLoading || !promoCode.trim()}
            className="btn text-white bg-black border border-black px-4 py-2 rounded-full hover:bg-black/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Validar
          </button>
        </div>

        {promoLoading && (
          <p className="mt-2 text-xs text-gray-500 flex items-center gap-1.5">
            <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
            Verificando código...
          </p>
        )}
        {promoError && <p className="mt-2 text-xs text-red-500">{promoError}</p>}
        {appliedPromo && <p className="mt-2 text-xs text-green-600">¡Cupón aplicado! Descuento de S/ {(promoDiscount).toFixed(2)}</p>}

        {/* <div className="mt-4">
          <label className={`flex items-start gap-3 rounded-xl border border-gray-400 bg-gray-50 p-3 text-xs`}>
            <input
              type="checkbox"
              checked={isSmartFitSelected}
              onChange={() => setIsSmartFitSelected(!isSmartFitSelected)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-black"
            />
            <span className="text-gray-700">
              Agregar add-on Smart Fit a esta compra.
            </span>
          </label>
        </div>


        <div className={`mt-4 rounded-2xl border border-gray-400 bg-prosper/10 p-4 ${!isSmartFitSelected ? 'opacity-50' : ''}`}>
          <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider">
            <span className="text-gray-500">Add-on Smart Fit</span>
            <span className="text-gray-800">Compra única</span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-16 shrink-0 items-center justify-center rounded-md bg-black px-1.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                <img src="/user/smartfit-logo.png" alt="Smart Fit Logo" className="h-auto w-15" />
              </div>
              <div>
                <div className="font-semibold">Smart Fit Plan Black · {smartFitDuration}</div>
                <div className="text-xs text-gray-500">+95 partners · +2,000 gimnasios · 15 días para activar</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-serif text-base font-semibold">S/ {smartFitPrice}</div>
              <div className="text-[10px] font-semibold text-gray-700">pago único · no recurrente</div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            {(['1 mes', '3 meses'] as const).map((duration) => (
              <button
                key={duration}
                type="button"
                disabled={!isSmartFitSelected}
                onClick={() => setSmartFitDuration(duration)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${!isSmartFitSelected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${smartFitDuration === duration
                  ? 'bg-prosper text-prosper-foreground cursor-normal'
                  : 'border-border bg-background hover:bg-muted/50'
                  }`}
              >
                {duration} · S/{duration === '1 mes' ? '129' : '348'}
              </button>
            ))}
          </div>
        </div> */}

        {/* {isSmartFitSelected && <div className="mt-3 flex items-start gap-2 rounded-xl border border-gray-300 bg-gray-50 p-3 text-xs text-gray-800">
          <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            El add-on Smart Fit es un <span className="font-semibold">pago único</span>, no se renueva automáticamente.
            Tienes <span className="font-semibold">15 días</span> desde la compra para activar tu membresía en cualquier sede Smart Fit.
          </span>
        </div>} */}
      </div>

      <PaymentSummaryCard
        selectedPaymentOption={selectedPaymentOption}
        planName={selectedPlan?.name}
        holosMonthlyCharge={holosMonthlyCharge}
        hasPromo={Boolean(appliedPromo)}
        promoDiscount={promoDiscount}
        cycleDuration={selectedPlan?.cycleDuration ?? 1}
        // originalChargeToday={price ?? 0}
        chargeToday={chargeToday}
        // isSmartFitSelected={isSmartFitSelected}
        // smartFitDuration={smartFitDuration}
        // smartFitPrice={smartFitPrice}
      />

      {/* {selectedPlan && <div className="text-sm mt-2">
        <strong>Antes de continuar, confirma que entiendes que:</strong>
        <br />
        <ul className="list-disc list-inside mb-2">
          <li>Holos es una suscripción con cobro autorenovable.</li>
          <li>Holos es únicamente para mayores de 18 años.</li>
          <li>Puedes cancelar o cambiar de plan cuando quieras.</li>
          <li>Tus datos y pagos están protegidos.</li>
          <li>Mi membresía es instrasferible para terceros.</li>
          <li>
            Acepto los
            <a href="/terminos/" target="_blank" rel="noopener noreferrer" className="text-holos-dark"> Términos y Condiciones de Holos.</a>
          </li>
          {isSmartFitSelected && (
            <li>
              Acepto los
              <a href={SMART_FIT_TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-holos-dark"> Términos y Condiciones de Smart Fit <ExternalLink className="inline h-3 w-3" /></a>
            </li>
          )}
          <li>
            Visita
            <a href="/preguntas-frecuentes/" target="_blank" rel="noopener noreferrer" className="text-holos-dark"> Preguntas Frecuentes </a>
            antes de comprar si aún tienes dudas.
          </li>
        </ul>
      </div>} */}
    </div>
  )
}
